import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  addEdge,
  Background,
  BackgroundVariant,
  ConnectionMode,
  Controls,
  getNodesBounds,
  MarkerType,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import { toPng } from "html-to-image";
import "@xyflow/react/dist/style.css";
import { Button, Col, Form, Input, Modal, notification, Popconfirm, Radio, Row, Select, Skeleton, Space, Switch, Table, Tag, Tooltip } from "antd";
import { DownloadOutlined, DownOutlined, NodeIndexOutlined, SaveFilled, UpOutlined } from "@ant-design/icons";

import { createAssignments, getAssignments } from "../../API/assignments";
import {
  createControl,
  getCategories,
  getControls,
  getControlsArea,
  getFrameworks,
  getPriorities,
  getRisks,
  getTypes,
} from "../../API/controls";
import { getDependencyByCode } from "../../API/dependencies";
import { deleteWorkflow, getComponentTypes, getWorkflowByCode, updateWorkflow } from "../../API/workflows";
import BackButton from "../Common/BackButton/BackButton";
import BreadCrumb from "../Common/BreadCrumb/BreadCrumb";
import ChangeLog from "../Common/ChangeLog/ChangeLog";
import PageTitle from "../Common/PageTitle/PageTitle";
import { useAuth } from "../Context/AuthContext";
import ControlsModal from "../Controls/components/ControlsModal/ControlsModal";
import WorkflowEdge from "./components/WorkflowEdge/WorkflowEdge";
import WorkflowNode from "./components/WorkflowNode/WorkflowNode";
import { toApiPayload, toReactFlow } from "./utils/workflowAdapters";
import "./Processes.css";

const FALLBACK_COMPONENTS = [
  { name: "inicio", kind: "node", description: "Inicio", index: 1 },
  { name: "fin", kind: "node", description: "Fin", index: 2 },
  { name: "actividad", kind: "node", description: "Actividad", index: 3 },
  { name: "decision", kind: "node", description: "Decisión", index: 4 },
  { name: "subproceso", kind: "node", description: "Subproceso", index: 5 },
  { name: "documento", kind: "node", description: "Documento", index: 6 },
  { name: "transicion", kind: "edge", description: "Transición", index: 1 },
  { name: "condicional", kind: "edge", description: "Condicional", index: 2 },
];

const VISUAL_TOOLS = [
  { key: "swimlane", label: "Carril", shape: "swimlane", preferredType: "carril" },
  { key: "start", label: "Inicio", shape: "start", preferredType: "inicio" },
  { key: "manual-process", label: "Paso de proceso manual", shape: "manual-process", preferredType: "actividad" },
  { key: "automated-process", label: "Paso de proceso automatizado", shape: "automated-process", preferredType: "actividad" },
  { key: "process-reference", label: "Referencia a proceso", shape: "process-reference", preferredType: "subproceso" },
  { key: "output-data", label: "Datos de salida / IPE", shape: "output-data", preferredType: "documento" },
  { key: "off-page-reference", label: "Referencia fuera de página", shape: "off-page-reference", preferredType: "subproceso" },
  { key: "end", label: "Fin", shape: "end", preferredType: "fin" },
  { key: "multi-document", label: "Multidocumento", shape: "multi-document", preferredType: "documento" },
  { key: "document", label: "Documento", shape: "document", preferredType: "documento" },
  { key: "external-data", label: "Datos externos utilizados", shape: "external-data", preferredType: "documento" },
  { key: "system-application", label: "Sistema / Aplicación", shape: "system-application", preferredType: "documento" },
  { key: "api", label: "API", shape: "api", preferredType: "actividad" },
  { key: "diamond", label: "Decisión", shape: "diamond", preferredType: "decision" },
  { key: "manual-input", label: "Entrada manual", shape: "manual-input", preferredType: "actividad" },
  { key: "display", label: "Pantalla", shape: "display", preferredType: "actividad" },
  { key: "rectangle", label: "Rectángulo", shape: "rectangle", preferredType: "actividad" },
  { key: "circle", label: "Círculo", shape: "circle", preferredType: "actividad" },
  { key: "database", label: "Base de datos", shape: "database", preferredType: "documento" },
  { key: "text", label: "Cuadro de texto", shape: "text", preferredType: "actividad" },
];

const FEATURED_TOOL_KEYS = ["swimlane", "start", "end", "diamond", "rectangle", "database"];
const FEATURED_TOOLS = FEATURED_TOOL_KEYS.map((key) => VISUAL_TOOLS.find((tool) => tool.key === key));
const OTHER_TOOLS = VISUAL_TOOLS
  .filter((tool) => !FEATURED_TOOL_KEYS.includes(tool.key))
  .sort((first, second) => first.label.localeCompare(second.label, "es"));

const getDefaultNodeColors = (shape) => {
  if (shape === "start") {
    return {
      backgroundColor: "#91d500",
      borderColor: "#ffffff",
      transparentBackground: false,
      transparentBorder: true,
    };
  }
  if (shape === "end") {
    return {
      backgroundColor: "#e53935",
      borderColor: "#c62828",
      transparentBackground: false,
      transparentBorder: false,
    };
  }
  if (shape === "automated-process") {
    return {
      backgroundColor: "#d0d0d0",
      borderColor: "#59616c",
      transparentBackground: false,
      transparentBorder: false,
    };
  }
  return {
    backgroundColor: "#ffffff",
    borderColor: "#006939",
    transparentBackground: false,
    transparentBorder: false,
  };
};

const getErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return error?.message || "Ocurrió un error inesperado.";
  if (typeof data === "string") return data;
  if (typeof data.detail === "string") return data.detail;
  if (data.non_field_errors?.length) return data.non_field_errors[0];
  for (const [field, value] of Object.entries(data)) {
    if (Array.isArray(value)) {
      const first = value[0];
      if (typeof first === "string") return first;
      if (first && typeof first === "object") {
        const nested = Object.values(first)[0];
        return `${field}: ${Array.isArray(nested) ? nested[0] : nested}`;
      }
    }
  }
  return "No se pudo guardar el workflow.";
};

const waitForEditorPaint = () => new Promise((resolve) => {
  requestAnimationFrame(() => requestAnimationFrame(resolve));
});

const sanitizeFilename = (value) => {
  const filename = (value || "diagrama-de-flujo")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9-_]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return filename || "diagrama-de-flujo";
};

const normalizeAssignment = (assignment) => ({
  ...assignment,
  code: assignment.code || assignment.dependency_control?.code,
  dependency: assignment.dependency || assignment.dependency_control?.dependency,
  control: assignment.control || assignment.dependency_control?.control,
});

const getDependencyAssignments = (response, dependencyCode) => {
  const assignments = Array.isArray(response) ? response : response?.results || [];
  return assignments
    .map(normalizeAssignment)
    .filter((assignment) => assignment.code
      && assignment.flag !== false
      && assignment.is_active !== false
      && (assignment.dependency?.code === dependencyCode || assignment.dependency_code === dependencyCode));
};

const isSwimlaneNode = (node) => node?.type === "carril" || node?.data?.shape === "swimlane";

const findContainingSwimlane = (nodes, position, excludedNodeId) => nodes.find((node) => {
  if (node.id === excludedNodeId || !isSwimlaneNode(node) || node.parentId) return false;
  const width = node.width || node.style?.width || 1100;
  const height = node.height || node.style?.height || 220;
  return position.x >= node.position.x
    && position.x <= node.position.x + Number(width)
    && position.y >= node.position.y
    && position.y <= node.position.y + Number(height);
});

const WorkflowEditorCanvas = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewOnly = searchParams.get("mode") === "view";
  const { hasPermission, userData } = useAuth();
  const { screenToFlowPosition, getViewport, setViewport } = useReactFlow();
  const [api, contextHolder] = notification.useNotification();
  const [workflow, setWorkflow] = useState(null);
  const [process, setProcess] = useState(null);
  const [subprocess, setSubprocess] = useState(null);
  const [componentTypes, setComponentTypes] = useState(FALLBACK_COMPONENTS);
  const [supportsCarrilType, setSupportsCarrilType] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [emptyWorkflowDraft, setEmptyWorkflowDraft] = useState(false);
  const [editingNodeId, setEditingNodeId] = useState(null);
  const [editingEdgeId, setEditingEdgeId] = useState(null);
  const [controlNodeId, setControlNodeId] = useState(null);
  const [availableControls, setAvailableControls] = useState([]);
  const [controlCatalog, setControlCatalog] = useState([]);
  const [controlsLoading, setControlsLoading] = useState(false);
  const [selectedCatalogControlCode, setSelectedCatalogControlCode] = useState(null);
  const [controlSearch, setControlSearch] = useState("");
  const [creatingControl, setCreatingControl] = useState(false);
  const [controlOptions, setControlOptions] = useState({
    areas: [],
    categories: [],
    priorities: [],
    frameworks: [],
    types: [],
    risks: [],
  });
  const [form] = Form.useForm();
  const [controlForm] = Form.useForm();
  const initialized = useRef(false);
  const canvasRef = useRef(null);
  const editorShellRef = useRef(null);
  const paletteRef = useRef(null);
  const paletteDrag = useRef(null);
  const [palettePosition, setPalettePosition] = useState({ x: 16, y: 16 });
  const [paletteMinimized, setPaletteMinimized] = useState(false);
  const [paletteDragging, setPaletteDragging] = useState(false);

  const isSuperuser = userData?.is_superuser === true;
  const canCreateControls = isSuperuser || (userData
    ? hasPermission("controls", "create")
    : false);

  const nodeTypes = useMemo(() => Object.fromEntries(
    componentTypes.filter((item) => item.kind === "node").map((item) => [item.name, WorkflowNode])
  ), [componentTypes]);
  const edgeTypes = useMemo(() => Object.fromEntries(
    componentTypes.filter((item) => item.kind === "edge").map((item) => [item.name, WorkflowEdge])
  ), [componentTypes]);
  const edgePalette = componentTypes.filter((item) => item.kind === "edge").sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  const defaultEdgeType = edgePalette.find((item) => item.name === "transicion")?.name || edgePalette[0]?.name || "transicion";

  const openControlSelector = useCallback((nodeId, linkedControlCode = null) => {
    setControlNodeId(nodeId);
    const linkedAssignment = availableControls.find((assignment) => assignment.code === linkedControlCode);
    setSelectedCatalogControlCode(linkedAssignment?.control?.code || null);
    setControlSearch("");
  }, [availableControls]);

  useEffect(() => {
    const load = async () => {
      initialized.current = false;
      setDirty(false);
      setLoading(true);
      try {
        const workflowData = await getWorkflowByCode(code);
        const dependencyCode = workflowData.dependency?.code;
        if (!dependencyCode) throw new Error("El workflow no tiene un subproceso asociado.");
        const [dependencyResult, catalogResult, assignmentsResult, controlsResult] = await Promise.allSettled([
          getDependencyByCode(dependencyCode),
          getComponentTypes(),
          getAssignments({ dependency: dependencyCode }),
          getControls(),
        ]);
        if (dependencyResult.status === "rejected") throw dependencyResult.reason;
        const dependency = dependencyResult.value;
        const asset = dependency.asset || workflowData.dependency?.asset;
        if (dependency.allows_workflow !== true && asset?.category?.allows_workflow !== true) {
          throw new Error("El activo del workflow no pertenece a una categoría habilitada para diagramas.");
        }
        const catalog = catalogResult.status === "fulfilled" && catalogResult.value.length
          ? catalogResult.value
          : FALLBACK_COMPONENTS;
        const supportsCarril = catalog.some((item) => item.name === "carril");
        const assignments = assignmentsResult.status === "fulfilled"
          ? getDependencyAssignments(assignmentsResult.value, dependencyCode)
          : [];
        const flow = toReactFlow(workflowData);
        setEmptyWorkflowDraft(!viewOnly && flow.nodes.length === 0);
        const assignmentMap = new Map(assignments.map((assignment) => [
          assignment.code,
          assignment.control,
        ]));
        setWorkflow(workflowData);
        setProcess(asset);
        setSubprocess(dependency);
        setComponentTypes(catalog);
        setSupportsCarrilType(supportsCarril);
        setAvailableControls(assignments);
        if (controlsResult.status === "fulfilled") {
          const controls = Array.isArray(controlsResult.value)
            ? controlsResult.value
            : controlsResult.value?.results || [];
          setControlCatalog(controls.filter((control) => control.deleted !== true));
        }
        setNodes(flow.nodes
          .map((node) => ({
            ...node,
            type: isSwimlaneNode(node)
              ? (supportsCarril ? "carril" : node.type === "carril" ? "actividad" : node.type)
              : node.type,
            zIndex: isSwimlaneNode(node) ? 0 : 10,
            data: (() => {
              const linkedControl = assignmentMap.get(node.data.controlCode);
              return {
                ...node.data,
                ...(node.type === "carril" ? { shape: "swimlane" } : {}),
                controlTitle: linkedControl?.title || linkedControl?.name,
                riskId: linkedControl?.risk?.id ?? node.data.riskId ?? null,
                riskTitle: linkedControl?.risk?.name || null,
              };
            })(),
          }))
          .sort((first, second) => Number(isSwimlaneNode(second)) - Number(isSwimlaneNode(first))));
        setEdges(flow.edges.map((edge) => ({
          ...edge,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: edge.type === "condicional" ? "#F5A623" : "#667085",
          },
        })));
        if (flow.viewport) setTimeout(() => setViewport(flow.viewport), 0);
        initialized.current = true;
        setDirty(false);
      } catch (error) {
        api.error({ message: "No se pudo cargar el workflow.", description: getErrorMessage(error) });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api, code, setEdges, setNodes, setViewport, viewOnly]);

  useEffect(() => {
    const warn = (event) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const markDirty = useCallback(() => {
    if (initialized.current && !viewOnly) setDirty(true);
  }, [viewOnly]);

  const clampPalettePosition = useCallback((x, y) => {
    const shell = editorShellRef.current;
    const palette = paletteRef.current;
    if (!shell || !palette) return { x, y };
    const padding = 8;
    const maxX = Math.max(padding, shell.clientWidth - palette.offsetWidth - padding);
    const maxY = Math.max(padding, shell.clientHeight - palette.offsetHeight - padding);
    return {
      x: Math.min(Math.max(padding, x), maxX),
      y: Math.min(Math.max(padding, y), maxY),
    };
  }, []);

  useEffect(() => {
    const keepInsideShell = () => {
      setPalettePosition((position) => clampPalettePosition(position.x, position.y));
    };
    const animationFrame = requestAnimationFrame(keepInsideShell);
    window.addEventListener("resize", keepInsideShell);
    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", keepInsideShell);
    };
  }, [clampPalettePosition, paletteMinimized]);

  const startPaletteDrag = (event) => {
    if (event.button !== 0) return;
    const paletteBounds = paletteRef.current?.getBoundingClientRect();
    if (!paletteBounds) return;
    paletteDrag.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - paletteBounds.left,
      offsetY: event.clientY - paletteBounds.top,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setPaletteDragging(true);
    event.preventDefault();
  };

  const movePalette = (event) => {
    const drag = paletteDrag.current;
    const shellBounds = editorShellRef.current?.getBoundingClientRect();
    if (!drag || drag.pointerId !== event.pointerId || !shellBounds) return;
    setPalettePosition(clampPalettePosition(
      event.clientX - shellBounds.left - drag.offsetX,
      event.clientY - shellBounds.top - drag.offsetY
    ));
  };

  const stopPaletteDrag = (event) => {
    if (paletteDrag.current?.pointerId !== event.pointerId) return;
    paletteDrag.current = null;
    setPaletteDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const resizeSwimlane = useCallback((nodeId, dimensions) => {
    setNodes((items) => items.map((node) => node.id === nodeId
      ? { ...node, ...dimensions }
      : node));
    markDirty();
  }, [markDirty, setNodes]);

  useEffect(() => {
    if (!workflow?.code) return;
    setNodes((items) => items.map((node) => ({
      ...node,
      data: {
        ...node.data,
        ...(isSwimlaneNode(node)
          ? {
              canResize: !viewOnly,
              onResizeSwimlane: viewOnly ? undefined : resizeSwimlane,
            }
          : {
              canAddControl: !viewOnly,
              onAddControl: viewOnly ? undefined : openControlSelector,
            }),
      },
    })));
  }, [openControlSelector, resizeSwimlane, setNodes, viewOnly, workflow?.code]);

  const handleNodesChange = useCallback((changes) => {
    onNodesChange(changes);
    // React Flow calcula las dimensiones de los nodos al montar el lienzo.
    // Esas mediciones internas no representan una edición del usuario.
    if (changes.some((change) => change.type === "remove")) markDirty();
  }, [markDirty, onNodesChange]);

  const handleEdgesChange = useCallback((changes) => {
    onEdgesChange(changes);
    if (changes.some((change) => change.type === "remove")) markDirty();
  }, [markDirty, onEdgesChange]);

  const onConnect = useCallback((connection) => {
    if (viewOnly) return;
    setEdges((currentEdges) => addEdge({
      ...connection,
      id: crypto.randomUUID(),
      type: defaultEdgeType,
      markerEnd: { type: MarkerType.ArrowClosed, color: "#667085" },
    }, currentEdges));
    markDirty();
  }, [defaultEdgeType, markDirty, setEdges, viewOnly]);

  const addNode = useCallback((tool, position) => {
    if (viewOnly) return;
    const availableNodeTypes = componentTypes.filter((item) => item.kind === "node");
    const isSwimlane = tool.shape === "swimlane";
    const catalogItem = isSwimlane
      ? (supportsCarrilType ? availableNodeTypes.find((item) => item.name === "carril") : null)
        || availableNodeTypes.find((item) => item.name === "actividad")
        || availableNodeTypes[0]
      : availableNodeTypes.find((item) => item.name === tool.preferredType) || availableNodeTypes[0];
    if (!catalogItem) return;
    const newNode = {
      id: crypto.randomUUID(),
      type: catalogItem.name,
      position,
      zIndex: isSwimlane ? 0 : 10,
      ...(isSwimlane ? { style: { width: 1100, height: 220 } } : {}),
      data: {
        label: tool.label,
        shape: tool.shape,
        colorRole: "neutral",
        ...(isSwimlane
          ? { canResize: true, onResizeSwimlane: resizeSwimlane }
          : {
              canAddControl: true,
              controlCode: null,
              controlTitle: null,
              riskId: null,
              riskTitle: null,
              onAddControl: openControlSelector,
            }),
      },
    };
    setNodes((currentNodes) => {
      if (isSwimlane) return [newNode, ...currentNodes];
      const parentLane = findContainingSwimlane(currentNodes, position, newNode.id);
      if (!parentLane) return currentNodes.concat(newNode);
      return currentNodes.concat({
        ...newNode,
        parentId: parentLane.id,
        extent: "parent",
        position: {
          x: position.x - parentLane.position.x,
          y: position.y - parentLane.position.y,
        },
      });
    });
    markDirty();
  }, [componentTypes, markDirty, openControlSelector, resizeSwimlane, setNodes, supportsCarrilType, viewOnly]);

  const handleNodeDragStart = useCallback((_, draggedNode) => {
    if (viewOnly || !draggedNode.parentId || isSwimlaneNode(draggedNode)) return;
    setNodes((currentNodes) => {
      const currentNode = currentNodes.find((node) => node.id === draggedNode.id);
      const parentLane = currentNodes.find((node) => node.id === currentNode?.parentId);
      if (!currentNode || !parentLane) return currentNodes;
      return currentNodes.map((node) => {
        if (node.id !== draggedNode.id) return node;
        const { parentId, extent, ...detachedNode } = node;
        return {
          ...detachedNode,
          position: {
            x: parentLane.position.x + node.position.x,
            y: parentLane.position.y + node.position.y,
          },
        };
      });
    });
  }, [setNodes, viewOnly]);

  const handleNodeDragStop = useCallback((_, draggedNode) => {
    if (viewOnly) return;
    if (isSwimlaneNode(draggedNode)) {
      markDirty();
      return;
    }
    setNodes((currentNodes) => {
      const currentNode = currentNodes.find((node) => node.id === draggedNode.id);
      if (!currentNode) return currentNodes;
      const currentParent = currentNodes.find((node) => node.id === currentNode.parentId);
      const absolutePosition = currentParent
        ? {
            x: currentParent.position.x + currentNode.position.x,
            y: currentParent.position.y + currentNode.position.y,
          }
        : currentNode.position;
      const parentLane = findContainingSwimlane(currentNodes, absolutePosition, draggedNode.id);
      if (!parentLane) return currentNodes;
      return currentNodes.map((node) => node.id === draggedNode.id
        ? {
            ...node,
            parentId: parentLane.id,
            extent: "parent",
            position: {
              x: absolutePosition.x - parentLane.position.x,
              y: absolutePosition.y - parentLane.position.y,
            },
          }
        : node);
    });
    markDirty();
  }, [markDirty, setNodes, viewOnly]);

  const onDrop = useCallback((event) => {
    event.preventDefault();
    if (viewOnly) return;
    const rawTool = event.dataTransfer.getData("application/reactflow");
    if (!rawTool) return;
    addNode(JSON.parse(rawTool), screenToFlowPosition({ x: event.clientX, y: event.clientY }));
  }, [addNode, screenToFlowPosition, viewOnly]);

  const openNodeEditor = (_, node) => {
    if (viewOnly) return;
    const shape = node.data?.shape || "rectangle";
    const defaultColors = getDefaultNodeColors(shape);
    setEditingNodeId(node.id);
    setEditingEdgeId(null);
    form.setFieldsValue({
      label: node.data?.label,
      shape,
      customColors: Boolean(node.data?.backgroundColor || node.data?.borderColor),
      backgroundColor: node.data?.backgroundColor === "transparent"
        ? defaultColors.backgroundColor
        : node.data?.backgroundColor || defaultColors.backgroundColor,
      borderColor: node.data?.borderColor === "transparent"
        ? defaultColors.borderColor
        : node.data?.borderColor || defaultColors.borderColor,
      transparentBackground: node.data?.backgroundColor === "transparent",
      transparentBorder: node.data?.borderColor === "transparent" || (
        !node.data?.borderColor && defaultColors.transparentBorder
      ),
    });
  };

  const handleShapeChange = (shape) => {
    if (!form.getFieldValue("customColors")) {
      form.setFieldsValue(getDefaultNodeColors(shape));
    }
  };

  const openEdgeEditor = (_, edge) => {
    if (viewOnly) return;
    setEditingEdgeId(edge.id);
    setEditingNodeId(null);
    form.setFieldsValue({ label: typeof edge.label === "string" ? edge.label : "", edgeType: edge.type });
  };

  const saveElement = (values) => {
    if (editingNodeId) {
      setNodes((items) => items.map((node) => {
        if (node.id !== editingNodeId) return node;
        const updatedData = {
          ...node.data,
          label: values.label,
          ...(node.data?.shape !== "swimlane" && values.shape
            ? { shape: values.shape }
            : {}),
        };

        if (values.customColors) {
          updatedData.backgroundColor = values.transparentBackground
            ? "transparent"
            : values.backgroundColor;
          updatedData.borderColor = values.transparentBorder
            ? "transparent"
            : values.borderColor;
        } else {
          delete updatedData.backgroundColor;
          delete updatedData.borderColor;
        }

        return { ...node, data: updatedData };
      }));
    }
    if (editingEdgeId) {
      setEdges((items) => items.map((edge) => edge.id === editingEdgeId ? {
        ...edge,
        label: values.label,
        type: values.edgeType,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: values.edgeType === "condicional" ? "#F5A623" : "#667085",
        },
      } : edge));
    }
    setEditingNodeId(null);
    setEditingEdgeId(null);
    form.resetFields();
    markDirty();
  };

  const handleSave = async () => {
    if (viewOnly) return;
    setSaving(true);
    try {
      if (nodes.length === 0) {
        await deleteWorkflow(workflow.code);
        setDirty(false);
        setEmptyWorkflowDraft(false);
        api.success({ message: "El diagrama vacío fue descartado." });
        navigate("/processes");
        return;
      }

      const payload = toApiPayload({
        dependencyCode: workflow.dependency?.code || subprocess?.code,
        name: workflow.name,
        description: workflow.description,
        nodes: nodes.map((node) => node.type === "carril" && !supportsCarrilType
          ? { ...node, type: "actividad" }
          : node),
        edges,
        viewport: getViewport(),
        expectedUpdated: workflow.updated,
      });
      const saved = await updateWorkflow(workflow.code, payload);
      try {
        setWorkflow(await getWorkflowByCode(workflow.code));
      } catch {
        setWorkflow((current) => ({ ...current, ...saved, logs: saved?.logs || current?.logs || [] }));
      }
      api.success({ message: "Diagrama guardado." });
      setEmptyWorkflowDraft(false);
      setDirty(false);
    } catch (error) {
      if (error?.response?.status === 409) {
        Modal.confirm({
          title: "El diagrama fue modificado",
          content: getErrorMessage(error),
          okText: "Recargar",
          cancelText: "Seguir editando",
          onOk: () => window.location.reload(),
        });
        return;
      }
      api.error({ message: "Error al guardar el diagrama.", description: getErrorMessage(error) });
    } finally {
      setSaving(false);
    }
  };

  const handleExportPng = async () => {
    if (!nodes.length) {
      api.warning({ message: "No hay elementos para exportar." });
      return;
    }

    setExporting(true);
    try {
      await waitForEditorPaint();
      const viewport = canvasRef.current?.querySelector(".react-flow__viewport");
      if (!viewport) throw new Error("No se encontró el lienzo del diagrama.");

      const bounds = getNodesBounds(nodes);
      const padding = 100;
      const contentWidth = Math.max(1, bounds.width + (padding * 2));
      const contentHeight = Math.max(1, bounds.height + (padding * 2));
      const maxLogicalSize = 3000;
      const exportScale = Math.min(1, maxLogicalSize / contentWidth, maxLogicalSize / contentHeight);
      const imageWidth = Math.ceil(contentWidth * exportScale);
      const imageHeight = Math.ceil(contentHeight * exportScale);

      const dataUrl = await toPng(viewport, {
        backgroundColor: "#ffffff",
        cacheBust: true,
        pixelRatio: 2,
        width: imageWidth,
        height: imageHeight,
        style: {
          width: `${imageWidth}px`,
          height: `${imageHeight}px`,
          transformOrigin: "top left",
          transform: `translate(${(-bounds.x + padding) * exportScale}px, ${(-bounds.y + padding) * exportScale}px) scale(${exportScale})`,
        },
        filter: (element) => {
          const classList = element?.classList;
          if (!classList) return true;
          return !classList.contains("react-flow__handle")
            && !classList.contains("workflow-node__add-control")
            && !classList.contains("workflow-swimlane__resize-control");
        },
      });

      const downloadLink = document.createElement("a");
      downloadLink.download = `${sanitizeFilename(workflow?.name)}.png`;
      downloadLink.href = dataUrl;
      downloadLink.click();
      api.success({ message: "Diagrama exportado como PNG." });
    } catch (error) {
      api.error({
        message: "No se pudo exportar el diagrama.",
        description: error?.message || "Ocurrió un error al generar la imagen.",
      });
    } finally {
      setExporting(false);
    }
  };

  const closeControlSelector = () => {
    setControlNodeId(null);
    setSelectedCatalogControlCode(null);
    setControlSearch("");
  };

  const linkControlToNode = (assignment) => {
    if (!controlNodeId) return;
    setNodes((items) => items.map((node) => {
      if (node.id !== controlNodeId) return node;
      return {
        ...node,
        data: {
          ...node.data,
          controlCode: assignment?.code || null,
          controlTitle: assignment?.control?.title
            || assignment?.control?.name
            || null,
          riskId: assignment?.control?.risk?.id ?? null,
          riskTitle: assignment?.control?.risk?.name || null,
        },
      };
    }));
    markDirty();
    closeControlSelector();
  };

  const getOrCreateAssignment = async (control) => {
    const existing = availableControls.find((assignment) => assignment.control?.code === control.code);
    if (existing) return existing;

    const response = await createAssignments([{
      dependency_code: subprocess.code,
      control_code: control.code,
      flag: true,
    }]);
    const createdItems = Array.isArray(response)
      ? response
      : Array.isArray(response?.results) ? response.results : [response];
    let created = createdItems.map(normalizeAssignment).find((assignment) => assignment.code);

    if (!created) {
      const refreshed = getDependencyAssignments(
        await getAssignments({ dependency: subprocess.code }),
        subprocess.code
      );
      created = refreshed.find((assignment) => assignment.control?.code === control.code);
    }
    if (!created?.code) throw new Error("La asignación se creó, pero la API no devolvió su código.");

    const normalized = {
      ...created,
      dependency: created.dependency || subprocess,
      control: {
        ...control,
        ...(created.control || {}),
        risk: created.control?.risk || control.risk || null,
      },
    };
    setAvailableControls((items) => [...items, normalized]);
    return normalized;
  };

  const saveNodeControl = async () => {
    if (!controlNodeId) return;
    if (!selectedCatalogControlCode) {
      linkControlToNode(null);
      return;
    }

    const control = controlCatalog.find((item) => item.code === selectedCatalogControlCode);
    if (!control) return;
    setControlsLoading(true);
    try {
      const assignment = await getOrCreateAssignment(control);
      linkControlToNode(assignment);
    } catch (error) {
      api.error({
        message: "No se pudo asociar el control al subproceso.",
        description: getErrorMessage(error),
      });
    } finally {
      setControlsLoading(false);
    }
  };

  const openCreateControl = async () => {
    setControlsLoading(true);
    try {
      const [areas, categories, priorities, frameworks, types, risks] = await Promise.all([
        getControlsArea("controls"),
        getCategories(),
        getPriorities(),
        getFrameworks(),
        getTypes(),
        getRisks(),
      ]);
      setControlOptions({ areas, categories, priorities, frameworks, types, risks });
      controlForm.resetFields();
      setCreatingControl(true);
    } catch (error) {
      api.error({
        message: "No se pudo abrir el formulario de control.",
        description: getErrorMessage(error),
      });
    } finally {
      setControlsLoading(false);
    }
  };

  const createAndLinkControl = async (values) => {
    setControlsLoading(true);
    let control = null;
    try {
      control = await createControl({
        title: values.title,
        description: values.description,
        area_id: values.area,
        category_id: values.category,
        priority_id: values.priority,
        framework_id: values.framework,
        type_id: values.type,
        risk_id: values.risk,
      });
      setControlCatalog((items) => [...items, control]);
      const assignment = await getOrCreateAssignment(control);
      setCreatingControl(false);
      controlForm.resetFields();
      linkControlToNode(assignment);
      api.success({ message: "Control creado y asociado al elemento." });
    } catch (error) {
      if (control) {
        setCreatingControl(false);
        controlForm.resetFields();
        setSelectedCatalogControlCode(control.code);
      }
      api.error({
        message: control
          ? "El control se creó, pero no pudo asociarse al subproceso."
          : "No se pudo crear el control.",
        description: getErrorMessage(error),
      });
    } finally {
      setControlsLoading(false);
    }
  };

  const leaveEditor = async () => {
    if (emptyWorkflowDraft) {
      try {
        await deleteWorkflow(workflow.code);
        setEmptyWorkflowDraft(false);
      } catch (error) {
        api.error({
          message: "No se pudo descartar el diagrama vacío.",
          description: getErrorMessage(error),
        });
        return;
      }
    }
    navigate("/processes");
  };

  const leaveConfirmation = {
    title: "¿Salir del editor?",
    description: "Hay cambios sin guardar. Si salís ahora, se perderán.",
    okText: "Salir",
    cancelText: "Continuar editando",
    onConfirm: leaveEditor,
    overlayClassName: "custom-popconfirm",
  };

  const editingNode = nodes.find((node) => node.id === editingNodeId);
  const editingSwimlane = editingNode?.data?.shape === "swimlane";
  const filteredControls = controlCatalog.filter((control) => {
    const search = controlSearch.trim().toLowerCase();
    if (!search) return true;
    return [control.title, control.description, control.area?.name, control.risk?.name]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(search));
  });
  const breadCrumbUrl = [
    { label: "Procesos", url: "/processes" },
    { label: process?.name || "Cargando..." },
    { label: subprocess?.name || "Subproceso" },
    { label: workflow?.name || "Diagrama de flujo" },
  ];

  if (loading) {
    return (
      <>
        {contextHolder}
        <div className="workflow-editor-skeleton">
          <div className="workflow-editor-skeleton__header">
            <Skeleton.Input active size="large" />
            <Space>
              <Skeleton.Button active />
              <Skeleton.Button active />
              <Skeleton.Button active />
            </Space>
          </div>
          <div className="workflow-editor-skeleton__breadcrumb">
            <Skeleton.Button active shape="circle" size="small" />
            <Skeleton.Input active size="small" />
          </div>
          <div className="workflow-editor-shell workflow-editor-skeleton__shell">
            <aside className="workflow-palette workflow-editor-skeleton__palette">
              <Skeleton.Input active size="small" />
              <Skeleton active title={false} paragraph={{ rows: 1 }} />
              {Array.from({ length: 7 }, (_, index) => (
                <Skeleton.Button key={index} active block />
              ))}
            </aside>
            <div className="workflow-editor-skeleton__canvas">
              <Skeleton.Node active className="workflow-editor-skeleton__node workflow-editor-skeleton__node--start" />
              <Skeleton.Node active className="workflow-editor-skeleton__node workflow-editor-skeleton__node--activity" />
              <Skeleton.Node active className="workflow-editor-skeleton__node workflow-editor-skeleton__node--decision" />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {contextHolder}
      <div className="workflow-editor-page">
        <Row justify="space-between" align="middle">
          <Col>
            <Space align="center">
              <PageTitle title="Diagrama de flujo" icon="processes" />
              {viewOnly && <Tag color="orange">Solo lectura</Tag>}
            </Space>
          </Col>
          <Col>
            <Space>
              <ChangeLog data={{ ...workflow, area: process?.area }} logs={workflow?.logs || []} />
              <Button
                icon={<DownloadOutlined />}
                className="save-type-button workflow-export-button"
                type="primary"
                loading={exporting}
                disabled={!nodes.length}
                onClick={handleExportPng}
              >
                Exportar
              </Button>
              {!viewOnly && (
                <Button icon={<SaveFilled />} className="save-type-button" loading={saving} disabled={!dirty} onClick={handleSave}>Guardar</Button>
              )}
            </Space>
          </Col>
        </Row>
        <Row justify="start" align="middle" gutter={16} className="workflow-editor-breadcrumb">
          <Col style={{ display: "flex", alignItems: "center" }}>
            {dirty ? (
              <Popconfirm {...leaveConfirmation}>
                <span className="workflow-editor-back-trigger"><BackButton onConfirm={() => {}} /></span>
              </Popconfirm>
            ) : (
              <BackButton onConfirm={leaveEditor} />
            )}
            <BreadCrumb location={breadCrumbUrl} activeKey={subprocess?.code || code} />
          </Col>
        </Row>

        <div ref={editorShellRef} className="workflow-editor-shell">
          {!viewOnly && (
            <aside
              ref={paletteRef}
              className={`workflow-palette ${paletteMinimized ? "is-minimized" : ""} ${paletteDragging ? "is-dragging" : ""}`}
              style={{ left: palettePosition.x, top: palettePosition.y }}
            >
              <div
                className="workflow-palette__title"
                onPointerDown={startPaletteDrag}
                onPointerMove={movePalette}
                onPointerUp={stopPaletteDrag}
                onPointerCancel={stopPaletteDrag}
              >
                <span><NodeIndexOutlined /> Elementos</span>
                <Tooltip title={paletteMinimized ? "Mostrar elementos" : "Minimizar elementos"}>
                  <button
                    type="button"
                    className="workflow-palette__toggle"
                    aria-label={paletteMinimized ? "Mostrar elementos" : "Minimizar elementos"}
                    aria-expanded={!paletteMinimized}
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={() => setPaletteMinimized((value) => !value)}
                  >
                    {paletteMinimized ? <DownOutlined /> : <UpOutlined />}
                  </button>
                </Tooltip>
              </div>
              {!paletteMinimized && (
                <>
                  <div className="workflow-palette__nodes">
                {[
                  { title: "Más utilizados", tools: FEATURED_TOOLS },
                  { title: "Otros elementos", tools: OTHER_TOOLS },
                ].map((section) => (
                  <React.Fragment key={section.title}>
                    <div className="workflow-palette__section">{section.title}</div>
                    {section.tools.map((tool) => (
                      <button
                        key={tool.key}
                        type="button"
                        className={`workflow-palette-item workflow-palette-item--${tool.shape}`}
                        draggable
                        onDragStart={(event) => {
                          event.dataTransfer.setData("application/reactflow", JSON.stringify(tool));
                          event.dataTransfer.effectAllowed = "move";
                        }}
                        onClick={() => addNode(tool, { x: 280 + nodes.length * 24, y: 80 + nodes.length * 24 })}
                      >
                        <span className={`workflow-palette-shape workflow-palette-shape--${tool.shape}`} />
                        <span>{tool.label}</span>
                      </button>
                    ))}
                  </React.Fragment>
                ))}
                  </div>
                  <div className="workflow-palette__help">
                    <Tag color="green">Doble clic</Tag> edita el texto.<br />
                    <Tag>Delete</Tag> elimina la selección.
                  </div>
                </>
              )}
            </aside>
          )}

            <main ref={canvasRef} className="workflow-canvas" onDrop={onDrop} onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = "move"; }}>
              <ReactFlow
                nodes={exporting ? nodes.map((node) => ({ ...node, selected: false })) : nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                connectionMode={ConnectionMode.Loose}
                onNodesChange={handleNodesChange}
                onEdgesChange={handleEdgesChange}
                onConnect={viewOnly ? undefined : onConnect}
                onNodeDoubleClick={viewOnly ? undefined : openNodeEditor}
                onNodeDragStart={viewOnly ? undefined : handleNodeDragStart}
                onNodeDragStop={viewOnly ? undefined : handleNodeDragStop}
                onEdgeDoubleClick={viewOnly ? undefined : openEdgeEditor}
                nodesDraggable={!viewOnly}
                nodesConnectable={!viewOnly}
                edgesReconnectable={!viewOnly}
                elementsSelectable={!viewOnly}
                elevateNodesOnSelect={false}
                fitView={!workflow?.properties?.viewport}
                proOptions={{ hideAttribution: true }}
                deleteKeyCode={viewOnly ? null : ["Backspace", "Delete"]}
                minZoom={0.25}
                maxZoom={2}
              >
                <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#d0d5dd" />
                <Controls showInteractive={!viewOnly} />
              </ReactFlow>
            </main>
          </div>
      </div>

      <Modal
        className="dependency-modal workflow-element-modal"
        title={editingNodeId ? "Editar figura" : "Editar conector"}
        open={Boolean(editingNodeId || editingEdgeId)}
        onCancel={() => { setEditingNodeId(null); setEditingEdgeId(null); form.resetFields(); }}
        footer={[
          <Button
            key="cancel"
            className="cancel-button"
            onClick={() => { setEditingNodeId(null); setEditingEdgeId(null); form.resetFields(); }}
          >
            Cancelar
          </Button>,
          <Button
            key="save"
            className="save-type-button"
            icon={<SaveFilled />}
            onClick={() => form.submit()}
          >
            Guardar
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical" onFinish={saveElement}>
          <Form.Item name="label" label="Texto" rules={[{ required: Boolean(editingNodeId), whitespace: true, message: "Ingrese un texto" }]}>
            <Input.TextArea maxLength={500} showCount style={{ resize: "none" }} />
          </Form.Item>
          {editingNodeId && !editingSwimlane && (
            <>
              <Form.Item name="shape" label="Forma" rules={[{ required: true }]}>
                <Select onChange={handleShapeChange}>
                  {VISUAL_TOOLS.filter((tool) => !["swimlane", "start", "end"].includes(tool.shape)).map((tool) => (
                    <Select.Option key={tool.shape} value={tool.shape}>{tool.label}</Select.Option>
                  ))}
                  <Select.Option value="start">Inicio</Select.Option>
                  <Select.Option value="end">Fin</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="customColors" label="Estilo de colores" valuePropName="checked">
                <Switch
                  checkedChildren="Personalizado"
                  unCheckedChildren="Original"
                  onChange={(checked) => {
                    if (!checked) {
                      form.setFieldsValue(getDefaultNodeColors(form.getFieldValue("shape")));
                    }
                  }}
                />
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(previous, current) => previous.customColors !== current.customColors}>
                {({ getFieldValue }) => getFieldValue("customColors") && (
                  <div className="workflow-color-settings">
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldValue: getValue }) => (
                            <div className={`workflow-color-option ${getValue("transparentBackground") ? "is-transparent" : ""}`}>
                              <div className="workflow-color-option__header">
                                <span>Fondo</span>
                                <Form.Item name="transparentBackground" valuePropName="checked" noStyle>
                                  <Switch size="small" checkedChildren="Transparente" unCheckedChildren="Color" />
                                </Form.Item>
                              </div>
                              <div className="workflow-color-option__picker-row">
                                <Form.Item name="backgroundColor" noStyle>
                                  <input
                                    type="color"
                                    className="workflow-color-picker"
                                    aria-label="Seleccionar color de fondo"
                                    disabled={getValue("transparentBackground")}
                                  />
                                </Form.Item>
                                <span>{getValue("transparentBackground") ? "Transparente" : getValue("backgroundColor")}</span>
                              </div>
                            </div>
                          )}
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldValue: getValue }) => (
                            <div className={`workflow-color-option ${getValue("transparentBorder") ? "is-transparent" : ""}`}>
                              <div className="workflow-color-option__header">
                                <span>Contorno</span>
                                <Form.Item name="transparentBorder" valuePropName="checked" noStyle>
                                  <Switch size="small" checkedChildren="Transparente" unCheckedChildren="Color" />
                                </Form.Item>
                              </div>
                              <div className="workflow-color-option__picker-row">
                                <Form.Item name="borderColor" noStyle>
                                  <input
                                    type="color"
                                    className="workflow-color-picker"
                                    aria-label="Seleccionar color de contorno"
                                    disabled={getValue("transparentBorder")}
                                  />
                                </Form.Item>
                                <span>{getValue("transparentBorder") ? "Transparente" : getValue("borderColor")}</span>
                              </div>
                            </div>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                )}
              </Form.Item>
            </>
          )}
          {editingEdgeId && (
            <Form.Item name="edgeType" label="Tipo de conector" rules={[{ required: true }]}>
              <Select>{edgePalette.map((item) => <Select.Option key={item.name} value={item.name}>{item.description || item.name}</Select.Option>)}</Select>
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        className="dependency-modal workflow-element-modal workflow-controls-modal"
        title="Agregar control"
        open={Boolean(controlNodeId)}
        onCancel={closeControlSelector}
        width={900}
        centered
        footer={[
          <Button
            key="clear"
            className="workflow-controls-modal__clear"
            disabled={!selectedCatalogControlCode}
            onClick={() => {
              setSelectedCatalogControlCode(null);
            }}
          >
            Deseleccionar
          </Button>,
          <Button key="cancel" className="cancel-button" onClick={closeControlSelector}>
            Cancelar
          </Button>,
          <Button
            key="save"
            className="save-type-button"
            icon={<SaveFilled />}
            loading={controlsLoading}
            onClick={saveNodeControl}
          >
            Guardar
          </Button>,
        ]}
      >
        <div className="workflow-controls-modal__toolbar">
          <Input.Search
            allowClear
            placeholder="Buscar por nombre, descripción, responsable o riesgo"
            value={controlSearch}
            onChange={(event) => setControlSearch(event.target.value)}
            className="workflow-controls-modal__search"
          />
          <Tooltip title={!canCreateControls ? "No tiene permisos para crear controles" : ""}>
            <span>
              <Button
                className="add-button workflow-controls-modal__create"
                disabled={!canCreateControls}
                onClick={openCreateControl}
              >
                Crear control
              </Button>
            </span>
          </Tooltip>
        </div>
        <Table
          className="custom-pagination workflow-controls-modal__table"
          rowKey="code"
          size="small"
          loading={controlsLoading}
          dataSource={filteredControls}
          columns={[
            {
              title: "",
              key: "selection",
              width: 48,
              align: "center",
              className: "workflow-controls-modal__selection-column",
              render: (_, control) => (
                <Radio
                  checked={selectedCatalogControlCode === control.code}
                  onChange={() => setSelectedCatalogControlCode(control.code)}
                  aria-label={`Seleccionar ${control.title || control.name || "control"}`}
                />
              ),
            },
            {
              title: "Nombre",
              key: "title",
              ellipsis: true,
              render: (_, control) => control.title || control.name || "N/A",
            },
            {
              title: "Riesgo",
              key: "risk",
              width: 220,
              ellipsis: true,
              render: (_, control) => control.risk?.name || "N/A",
            },
            {
              title: "Responsable",
              key: "area",
              width: 180,
              render: (_, control) => control.area?.name || "N/A",
            },
          ]}
          onRow={(control) => ({
            className: selectedCatalogControlCode === control.code
              ? "workflow-controls-modal__row--selected"
              : "",
            onClick: () => setSelectedCatalogControlCode(control.code),
          })}
          pagination={{
            defaultPageSize: 5,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20"],
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
            showLessItems: true,
          }}
          locale={{
            emptyText: controlsLoading ? "Cargando controles..." : (
              <div className="workflow-controls-empty">
                <strong>No se encontraron controles.</strong>
                <span>Podés crear uno nuevo desde este mismo modal.</span>
              </div>
            ),
          }}
        />
      </Modal>

      <ControlsModal
        areas={controlOptions.areas}
        categories={controlOptions.categories}
        priorities={controlOptions.priorities}
        frameworks={controlOptions.frameworks}
        types={controlOptions.types}
        risks={controlOptions.risks}
        isModalOpen={creatingControl}
        setIsModalOpen={setCreatingControl}
        form={controlForm}
        handleSave={createAndLinkControl}
        editingControl={null}
      />
    </>
  );
};

const WorkflowEditor = () => (
  <ReactFlowProvider>
    <WorkflowEditorCanvas />
  </ReactFlowProvider>
);

export default WorkflowEditor;
